// src/Components/Broadcast/hooks/useUserFilter.js
import { useState, useEffect, useCallback, useRef } from 'react';

export const useUserFilter = (userList, setNotification) => {
  const [targetFilter, setTargetFilter] = useState('all');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const initializedRef = useRef(false);

  // Функция для проверки доступа к курсу
  const checkCourseAccess = useCallback((user, courseId) => {
    if (!user) return false;
    
    if (user.courses && Array.isArray(user.courses)) {
      if (user.courses.includes(courseId)) return true;
    }
    
    if (user.coursesAccess && Array.isArray(user.coursesAccess)) {
      for (const access of user.coursesAccess) {
        if (access && access.id === courseId) return true;
        if (access === courseId) return true;
      }
    }
    
    return false;
  }, []);

  // Функция для проверки доступа к тренингу
  const checkTrainingAccess = useCallback((user, trainingId) => {
    if (!user) return false;
    
    if (user.trainings && Array.isArray(user.trainings)) {
      if (user.trainings.includes(trainingId)) return true;
    }
    
    if (user.trainingsAccess && Array.isArray(user.trainingsAccess)) {
      for (const access of user.trainingsAccess) {
        if (access && access.id === trainingId) return true;
        if (access === trainingId) return true;
      }
    }
    
    return false;
  }, []);

  // НОВАЯ ФУНКЦИЯ: Проверка наличия подарка у пользователя
  const checkGiftAccess = useCallback((user, giftCode) => {
    if (!user) return false;
    
    // Проверяем по полю gifts (массив кодов подарков у пользователя)
    if (user.gifts && Array.isArray(user.gifts)) {
      // Проверяем, есть ли подарок с таким кодом
      const hasGift = user.gifts.some(g => {
        // Если gift - это объект с полем code
        if (typeof g === 'object' && g !== null) {
          return g.code === giftCode;
        }
        // Если gift - это строка (код)
        if (typeof g === 'string') {
          return g === giftCode;
        }
        return false;
      });
      if (hasGift) return true;
    }
    
    // Проверяем по полю giftIds (если есть)
    if (user.giftIds && Array.isArray(user.giftIds)) {
      if (user.giftIds.includes(giftCode)) return true;
    }
    
    return false;
  }, []);

  // Функция для получения пользователей из истории
  const getUsersFromHistory = useCallback((selectedHistoryItems, broadcastHistory, type) => {
    const usersSet = new Set();
    
    if (!selectedHistoryItems?.length || !broadcastHistory?.length) return [];
    
    selectedHistoryItems.forEach(itemId => {
      const historyItem = broadcastHistory.find(item => item.id === itemId);
      if (!historyItem) return;
      
      if (type === 'success' && historyItem.successUsers && Array.isArray(historyItem.successUsers)) {
        historyItem.successUsers.forEach(userId => {
          if (userId) usersSet.add(userId);
        });
      } else if (type === 'failed' && historyItem.failedUsers && Array.isArray(historyItem.failedUsers)) {
        historyItem.failedUsers.forEach(fail => {
          const userId = typeof fail === 'string' ? fail : fail?.id;
          if (userId) usersSet.add(userId);
        });
      }
    });
    
    return Array.from(usersSet);
  }, []);

  // Простая фильтрация по типу пользователя
  const filterByType = useCallback((users, filter) => {
    if (!users || !Array.isArray(users)) return [];
    
    switch (filter) {
      case 'premium':
        return users.filter(user => user.premium === true);
      case 'free':
        return users.filter(user => !user.premium);
      case 'selected':
        return users.filter(user => selectedUsers.includes(user.id));
      default:
        return [...users];
    }
  }, [selectedUsers]);

  // Применение дополнительных фильтров (продукты, исключения, история)
  const applyAdditionalFilters = useCallback((users, filters) => {
    if (!users || !Array.isArray(users)) return [];
    
    let result = [...users];
    
    console.log('applyAdditionalFilters - начальное количество:', result.length);
    console.log('applyAdditionalFilters - filters:', {
      combinationType: filters.combinationType,
      selectedGiftsCount: filters.giftFilter?.length || 0,
      selectedProductsCount: (filters.productFilter?.courses?.length || 0) + (filters.productFilter?.trainings?.length || 0),
      excludeByHistory: filters.excludeByHistory,
      selectedHistoryItemsCount: filters.selectedHistoryItems?.length,
      excludeSentUsers: filters.excludeSentUsers,
      sentUserIdsCount: filters.sentUserIds?.length,
      excludeSuccessUsers: filters.excludeSuccessUsers,
      excludeFailedUsers: filters.excludeFailedUsers,
      failedUsersOnly: filters.failedUsersOnly,
      forcedUsers: filters.forcedUsers?.length
    });
    
    // НОВОЕ: Режим "только получатели с ошибками"
    if (filters.failedUsersOnly && filters.forcedUsers && filters.forcedUsers.length > 0) {
      const beforeCount = result.length;
      result = result.filter(user => filters.forcedUsers.includes(user.id));
      console.log(`Режим "только ошибочные" (${filters.forcedUsers.length} пользователей): ${beforeCount} -> ${result.length}`);
      return result;
    }
    
    // ============================================
    // НОВАЯ ФИЛЬТРАЦИЯ ПО ПОДАРКАМ
    // ============================================
    if (filters.combinationType === 'has_gifts' || filters.combinationType === 'no_gifts') {
      const selectedGifts = filters.giftFilter || [];
      
      if (selectedGifts.length > 0) {
        // Собираем коды выбранных подарков
        const giftCodes = selectedGifts.map(g => g.code || g.id).filter(Boolean);
        console.log('🎁 Фильтрация по подаркам, выбрано кодов:', giftCodes.length, giftCodes);
        
        const hasGifts = (user) => {
          // Проверяем, есть ли у пользователя хотя бы один из выбранных подарков
          for (const giftCode of giftCodes) {
            if (checkGiftAccess(user, giftCode)) {
              return true;
            }
          }
          return false;
        };
        
        if (filters.combinationType === 'has_gifts') {
          const beforeCount = result.length;
          result = result.filter(user => hasGifts(user));
          console.log(`🎁 Фильтр has_gifts: ${beforeCount} -> ${result.length}`);
        } else if (filters.combinationType === 'no_gifts') {
          const beforeCount = result.length;
          result = result.filter(user => !hasGifts(user));
          console.log(`🎁 Фильтр no_gifts: ${beforeCount} -> ${result.length}`);
        }
      } else {
        console.log('🎁 Нет выбранных подарков для фильтрации');
        // Если не выбрано ни одного подарка, возвращаем пустой массив (или исходный?)
        // Лучше возвращать пустой, так как условие требует наличия подарков
        if (filters.combinationType === 'has_gifts') {
          console.log('🎁 has_gifts без выбора подарков -> 0 результатов');
          result = [];
        }
        // Для no_gifts без выбора подарков - оставляем всех
      }
    }
    
    // НОВОЕ: Фильтр "не получали НИ ОДНОГО подарка"
    if (filters.combinationType === 'no_gifts_any') {
      const beforeCount = result.length;
      result = result.filter(user => {
        // Проверяем, есть ли у пользователя хоть один подарок
        const hasAnyGift = (user.gifts && user.gifts.length > 0) || 
                          (user.giftIds && user.giftIds.length > 0);
        return !hasAnyGift;
      });
      console.log(`🎁 Фильтр no_gifts_any: ${beforeCount} -> ${result.length}`);
    }

    // ============================================
    // ФИЛЬТРАЦИЯ ПО ПРОДУКТАМ
    // ============================================
    if (filters.combinationType === 'has_products' || filters.combinationType === 'no_products') {
      const hasProducts = (user) => {
        const productFilter = filters.productFilter;
        if (!productFilter?.courses?.length && !productFilter?.trainings?.length) return false;
        
        if (productFilter.courses?.length > 0) {
          for (const courseId of productFilter.courses) {
            if (checkCourseAccess(user, courseId)) return true;
          }
        }
        
        if (productFilter.trainings?.length > 0) {
          for (const trainingId of productFilter.trainings) {
            if (checkTrainingAccess(user, trainingId)) return true;
          }
        }
        
        return false;
      };
      
      if (filters.combinationType === 'has_products') {
        const beforeCount = result.length;
        result = result.filter(user => hasProducts(user));
        console.log(`Фильтр has_products: ${beforeCount} -> ${result.length}`);
      } else if (filters.combinationType === 'no_products') {
        const beforeCount = result.length;
        result = result.filter(user => !hasProducts(user));
        console.log(`Фильтр no_products: ${beforeCount} -> ${result.length}`);
      }
    }
    
    // Фильтр для no_products_any
    if (filters.combinationType === 'no_products_any') {
      const beforeCount = result.length;
      result = result.filter(user => {
        const hasCourse = (user.courses?.length > 0) || (user.coursesAccess?.length > 0);
        const hasTraining = (user.trainings?.length > 0) || (user.trainingsAccess?.length > 0);
        return !hasCourse && !hasTraining;
      });
      console.log(`Фильтр no_products_any: ${beforeCount} -> ${result.length}`);
    }
    
    // ============================================
    // ИСКЛЮЧЕНИЯ
    // ============================================
    
    // Исключения пользователей по ID
    if (filters.excludedUsers?.length > 0) {
      const beforeCount = result.length;
      result = result.filter(u => !filters.excludedUsers.includes(u.id));
      console.log(`Исключено по ID (${filters.excludedUsers.length}): ${beforeCount} -> ${result.length}`);
    }
    
    // Исключения групп
    if (filters.excludedGroups?.length > 0) {
      const beforeCount = result.length;
      if (filters.excludedGroups.includes('premium')) {
        result = result.filter(u => !u.premium);
      }
      if (filters.excludedGroups.includes('free')) {
        result = result.filter(u => u.premium);
      }
      console.log(`Исключено по группам: ${beforeCount} -> ${result.length}`);
    }
    
    // Исключения продуктов
    if (filters.excludedProducts?.length > 0) {
      const beforeCount = result.length;
      const excludedCourses = filters.excludedProducts
        .filter(key => key.startsWith('course_'))
        .map(key => key.replace('course_', ''));
      
      const excludedTrainings = filters.excludedProducts
        .filter(key => key.startsWith('training_'))
        .map(key => key.replace('training_', ''));
      
      result = result.filter(user => {
        let hasExcludedAccess = false;
        
        for (const courseId of excludedCourses) {
          if (checkCourseAccess(user, courseId)) {
            hasExcludedAccess = true;
            break;
          }
        }
        
        if (!hasExcludedAccess) {
          for (const trainingId of excludedTrainings) {
            if (checkTrainingAccess(user, trainingId)) {
              hasExcludedAccess = true;
              break;
            }
          }
        }
        
        return !hasExcludedAccess;
      });
      console.log(`Исключено по продуктам: ${beforeCount} -> ${result.length}`);
    }
    
    // Исключение пользователей, которые уже получали рассылку (простой флаг)
    if (filters.excludeSentUsers && filters.sentUserIds?.length > 0) {
      const beforeCount = result.length;
      result = result.filter(user => !filters.sentUserIds.includes(user.id));
      console.log(`Исключено получивших рассылку (${filters.sentUserIds.length}): ${beforeCount} -> ${result.length}`);
    }
    
    // НОВОЕ: Исключение успешно получивших пользователей из истории
    if (filters.excludeSuccessUsers && filters.selectedHistoryItems?.length > 0 && filters.broadcastHistory) {
      const beforeCount = result.length;
      const successUsers = getUsersFromHistory(filters.selectedHistoryItems, filters.broadcastHistory, 'success');
      
      if (successUsers.length > 0) {
        result = result.filter(user => !successUsers.includes(user.id));
        console.log(`Исключено успешно получивших (${successUsers.length}): ${beforeCount} -> ${result.length}`);
      }
    }
    
    // НОВОЕ: Исключение пользователей с ошибками из истории
    if (filters.excludeFailedUsers && filters.selectedHistoryItems?.length > 0 && filters.broadcastHistory) {
      const beforeCount = result.length;
      const failedUsers = getUsersFromHistory(filters.selectedHistoryItems, filters.broadcastHistory, 'failed');
      
      if (failedUsers.length > 0) {
        result = result.filter(user => !failedUsers.includes(user.id));
        console.log(`Исключено с ошибками (${failedUsers.length}): ${beforeCount} -> ${result.length}`);
      }
    }
    
    // Старый способ: исключение всех пользователей из выбранных рассылок (для обратной совместимости)
    if (filters.excludeByHistory && filters.selectedHistoryItems?.length > 0 && filters.broadcastHistory) {
      const beforeCount = result.length;
      
      const usersToExclude = new Set();
      
      filters.selectedHistoryItems.forEach(itemId => {
        const historyItem = filters.broadcastHistory.find(item => item.id === itemId);
        if (historyItem && historyItem.successUsers && historyItem.successUsers.length > 0) {
          historyItem.successUsers.forEach(userId => {
            usersToExclude.add(userId);
          });
          if (historyItem.failedUsers && historyItem.failedUsers.length > 0) {
            historyItem.failedUsers.forEach(fail => {
              const userId = typeof fail === 'string' ? fail : fail?.id;
              if (userId) usersToExclude.add(userId);
            });
          }
        }
      });
      
      if (usersToExclude.size > 0) {
        result = result.filter(user => !usersToExclude.has(user.id));
        console.log(`Исключено из истории (${usersToExclude.size} уникальных пользователей): ${beforeCount} -> ${result.length}`);
      } else {
        console.log('Нет пользователей для исключения из истории');
      }
    }
    
    console.log('applyAdditionalFilters - итоговое количество:', result.length);
    
    return result;
  }, [checkCourseAccess, checkTrainingAccess, checkGiftAccess, getUsersFromHistory]);

  // Обновление фильтра по типу
  const handleFilterChange = useCallback((filter) => {
    console.log('handleFilterChange - new filter:', filter);
    setTargetFilter(filter);
    
    if (!userList || !Array.isArray(userList)) {
      setFilteredUsers([]);
      return;
    }
    
    const filtered = filterByType(userList, filter);
    console.log('handleFilterChange - filtered by type:', filtered.length);
    setFilteredUsers(filtered);
  }, [userList, filterByType]);

  // Обновление с учетом дополнительных фильтров
  const updateFilteredUsersWithFilters = useCallback((additionalFilters) => {
    if (!userList || userList.length === 0) return [];
    
    // Сначала фильтруем по типу
    const typeFiltered = filterByType(userList, targetFilter);
    console.log('updateFilteredUsersWithFilters - type filtered:', typeFiltered.length);
    console.log('updateFilteredUsersWithFilters - targetFilter:', targetFilter);
    
    // Затем применяем дополнительные фильтры
    const finalFiltered = applyAdditionalFilters(typeFiltered, additionalFilters);
    console.log('updateFilteredUsersWithFilters - final filtered:', finalFiltered.length);
    
    setFilteredUsers(finalFiltered);
    return finalFiltered;
  }, [userList, targetFilter, filterByType, applyAdditionalFilters]);

  // Выбор/отмена выбора пользователя
  const toggleUserSelection = useCallback((userId) => {
    setSelectedUsers(prev => {
      const newSelected = prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId];
      
      if (targetFilter === 'selected' && userList) {
        const filtered = userList.filter(user => newSelected.includes(user.id));
        setFilteredUsers(filtered);
      }
      
      return newSelected;
    });
  }, [targetFilter, userList]);

  // Выбрать всех отфильтрованных по поиску
  const selectAllFiltered = useCallback((filteredBySearch) => {
    if (!filteredBySearch || !Array.isArray(filteredBySearch)) return;
    const filteredIds = filteredBySearch.map(u => u.id);
    setSelectedUsers(prev => {
      const newSelected = [...new Set([...prev, ...filteredIds])];
      if (targetFilter === 'selected') {
        const filtered = userList.filter(user => newSelected.includes(user.id));
        setFilteredUsers(filtered);
      }
      return newSelected;
    });
  }, [targetFilter, userList]);

  // Очистить выбор
  const clearSelection = useCallback(() => {
    setSelectedUsers([]);
    if (targetFilter === 'selected') {
      setFilteredUsers([]);
    }
  }, [targetFilter]);

  // Фильтрация пользователей по поиску
  const getFilteredUsersBySearch = useCallback((searchTerm, usersToFilter = null) => {
    const users = usersToFilter || filteredUsers || [];
    if (!users.length) return [];
    if (!searchTerm?.trim()) return users;
    
    const searchLower = searchTerm.toLowerCase();
    return users.filter(user => {
      return (
        (user.fullName?.toLowerCase().includes(searchLower)) ||
        (user.username?.toLowerCase().includes(searchLower)) ||
        (user.id?.toLowerCase().includes(searchLower)) ||
        (user.phone?.includes(searchTerm)) ||
        (user.email?.toLowerCase().includes(searchLower))
      );
    });
  }, [filteredUsers]);

  // Инициализация
  useEffect(() => {
    if (userList?.length > 0 && !initializedRef.current) {
      const filtered = filterByType(userList, targetFilter);
      console.log('Initial filtered users:', filtered.length);
      setFilteredUsers(filtered);
      initializedRef.current = true;
    }
  }, [userList, targetFilter, filterByType]);

  // Обновление при изменении selectedUsers
  useEffect(() => {
    if (userList?.length > 0 && targetFilter !== 'selected') {
      const filtered = filterByType(userList, targetFilter);
      setFilteredUsers(filtered);
    }
  }, [selectedUsers, userList, targetFilter, filterByType]);

  return {
    targetFilter,
    selectedUsers,
    setSelectedUsers,
    filteredUsers,
    handleFilterChange,
    toggleUserSelection,
    selectAllFiltered,
    clearSelection,
    getFilteredUsersBySearch,
    updateFilteredUsersWithFilters
  };
};